<template>
    <div ref="map" id="ol-map-container" class="w-100" :style="{height: mapHeight}"></div>
</template>

<script>
    import * as $ from 'jquery';
    import * as moment from 'moment';
    import OpenLayersMap from 'ol/Map';
    import View from 'ol/View';
    import {defaults as defaultInteractions} from 'ol/interaction';
    import {defaults as defaultControls} from 'ol/control';
    import GoogleMapSatelliteLayer from "@/olMap/layer/GoogleMapSatellite";
    import GoogleMapSatelliteLabelLayer from '@/olMap/layer/GoogleMapSatelliteLabel';
    import Chb2FairwayLayer from '@/olMap/layer/Chb2Fairway';
    import VectorLayer from "ol/layer/Vector";
    import VectorSource from "ol/source/Vector";
    import {fromLonLat} from '@/olMap/coordUtils';
    import Plot from '@/services/plot';
    import {
        selectedStyle, createVesselStyle, createLabelLineStyle, createLabelStyle,
        createFeature, trailFeatures, vesselDisplayName, labelDirections, getLabelItem, getLabelRect, svg,
    } from '@/olMap/featureUtils';
    import {Icon, Style} from "ol/style";
    import {getPointResolution} from "ol/proj";
    import Units from "ol/proj/Units";

    export default {
        name: "PreviewMap",
        data() {
            return {
                mapHeight: "100vh",
            };
        },
        methods: {
            patchVesselDim(vessel) {
                if (vessel.length > 0 && vessel.width > 0 && vessel.length < 500 && vessel.width < 80
                    && vessel.length > vessel.width
                    && vessel.length >= (vessel.trail || 0) && vessel.width >= (vessel.left || 0)) {
                    vessel.dim = {
                        length: vessel.length,
                        width: vessel.width,
                        bow: vessel.length - (vessel.trail || 0),
                        stern: vessel.trail || 0,
                        port: vessel.left || 0,
                        starboard: vessel.width - (vessel.left || 0),
                    }
                }
            },
            select: async function(mmsi) {
                let selected = await Plot.loadVessel(mmsi, true);
                if (selected) {
                    this.patchVesselDim(selected);
                    selected.displayName = vesselDisplayName(selected);
                    selected.id = selected.mmsi;
                    selected.time = new Date(selected.updateTime).getTime();
                    let interval = new Date().getTime() - new Date(selected.time).getTime();
                    selected.recent = interval < 2 * 3600 * 1000;
                    selected.in24h = interval < 24 * 3600 * 1000;
                    selected.coordinate = fromLonLat([selected.lon, selected.lat]);
                    this.selectedItem = selected;
                    let view = this.map.getView();
                    view.setCenter(selected.coordinate);

                    let from = this.getDateText(moment().subtract(6, "day"));
                    let to = this.getDateText(moment());
                    let result = await Plot.vesselTrail(mmsi, from, to, true);
                    let trail = result && result.trail || [];
                    trail.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                    this.selectedItem.trail = trail;
                }
                this.updateSelectedVesselLayer();
            },
            getDateText(date) {
                return date ? moment(date).format("YYYY-MM-DD") : null;
            },
            getClientSize() {
                return [
                    window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                    window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
                ];
            },
            createVesselOutlineFeature(v, isSelected) {
                if (this.getRes && v.dim) {
                    let res = this.getRes(v.coordinate);
                    let dim = Object.assign({}, v.dim);
                    Object.getOwnPropertyNames(dim).forEach(f => dim[f] /= res);
                    let center = [
                        Math.ceil(Math.max(dim.port, dim.starboard)) + 1,
                        Math.ceil(Math.max(dim.bow, dim.stern)) + 1,
                    ];
                    let size = [center[0] * 2, center[1] * 2];
                    let offsetX = center[0] - dim.port;
                    let offsetY = center[1] - dim.bow;
                    let points = [
                        [dim.width / 2, 0],
                        [0, dim.length / 3],
                        [0, dim.length],
                        [dim.width, dim.length],
                        [dim.width, dim.length / 3],
                    ].map(p => [p[0] + offsetX, p[1] + offsetY]);
                    let fill = isSelected ? "rgba(255, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)";
                    let stroke = isSelected ? "red" : "black";
                    let xml = `<polygon points="${points.map(p => `${p[0]},${p[1]}`).join(" ")}" fill="${fill}" stroke="${stroke}"></polygon>`;
                    let head = ((!v.heading || v.sog >= 0.5) ? v.cog : v.heading) || 0;
                    let style = new Style({
                        image: new Icon({
                            src: svg(size[0], size[1], xml),
                            imgSize: size,
                            rotation: Math.PI * head / 180,
                        })
                    });
                    return createFeature(v.coordinate, style, {vessel: v});
                }
                return null;
            },
            async updateSelectedVesselLayer() {
                let features = [];
                if (this.selectedItem) {
                    let vessel = this.selectedItem;
                    let coordinate = vessel.coordinate;
                    let feature = this.createVesselOutlineFeature(vessel, true);
                    if (feature) {
                        features.push(feature);
                    }
                    features.push(createFeature(coordinate, selectedStyle));
                    features.push(createFeature(coordinate, createVesselStyle(vessel, true), {vessel}));
                    let text = vessel.displayName;
                    let occupiedRects = [];
                    let pixel = this.map.getPixelFromCoordinate(coordinate);
                    if (pixel) {
                        occupiedRects.push([pixel[0] - 17, pixel[1] - 17, pixel[0] + 17, pixel[1] + 17]);
                    }
                    if (text !== '——') {
                        let direction = labelDirections[0];
                        features.push(createFeature(coordinate, createLabelLineStyle(direction.angle, true, false)));
                        features.push(createFeature(coordinate, createLabelStyle({text, direction}, true, false)));
                        let labelItem = getLabelItem(vessel, true);
                        if (labelItem) {
                            occupiedRects.push(getLabelRect(labelItem, direction, this.map));
                        }
                    }
                    let trail = vessel.trail;
                    if (trail) {
                        let resolution = this.map.getView().getResolution();
                        features = features.concat(trailFeatures(trail, resolution, vessel, occupiedRects, this.map));
                    }
                }
                this.selectedVesselLayer.setSource(new VectorSource({features: features}));
            },
        },
        mounted() {
            this.sizeHandler = () => this.mapHeight = this.getClientSize()[1] + "px";
            window.onresize = this.sizeHandler;
            this.sizeHandler();

            $(document.body).css("overflow", "hidden");

            this.selectedVesselLayer = new VectorLayer({source: new VectorSource({features: []})});
            let center = fromLonLat([119.511392, 32.21177]);
            let zoom = 15;
            let pos = this.$route.query["pos"];
            if (pos) {
                let comps = pos.split(",");
                center = [+comps[0] || 0, +comps[1] || 0];
                zoom = +comps[2] || 0;
            }
            this.map = new OpenLayersMap({
                target: 'ol-map-container',
                interactions: defaultInteractions({
                    altShiftDragRotate: false,
                    pinchRotate: false,
                    doubleClickZoom: false,
                }),
                controls: defaultControls(),
                loadTilesWhileInteracting: true,
                view: new View({center, zoom, maxZoom: 19}),
                layers: [
                    new GoogleMapSatelliteLayer({visible: true}),
                    new Chb2FairwayLayer({visible: true}),
                    new GoogleMapSatelliteLabelLayer({visible: true}),
                    this.selectedVesselLayer,
                ],
            });
            this.map.on("moveend", () => {
                let view = this.map.getView();
                let zoom = view.getZoom();
                let projection = view.getProjection();
                let resolution = view.getResolutionForZoom(zoom);
                this.getRes = coordinate => getPointResolution(projection, resolution, coordinate, Units.METERS);
                this.updateSelectedVesselLayer();
            });

            this.select(+this.$route.query["mmsi"]);
        },
        destroyed() {
            $(document.body).css("overflow", "");
        },
    }
</script>

<style scoped>

</style>
